import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';
import {
  FiUsers,
  FiRefreshCw,
  FiX,
  FiCheck,
  FiAlertCircle,
  FiAward,
  FiToggleLeft,
  FiToggleRight,
} from 'react-icons/fi';

function AdminReferrals() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState({
    isActive: false,
    pointsPerReferral: 10,
    totalReferrals: 0,
    totalPointsAwarded: 0,
    activeReferrers: 0,
    moversWithPoints: 0,
  });
  const [settings, setSettings] = useState({
    isActive: false,
    pointsPerReferral: 10,
    description: '',
  });

  const reloadTimerRef = useRef(null);

  const loadData = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const [settingsRes, statsRes, leaderboardRes] = await Promise.all([
        api.getReferralSettings(),
        api.getReferralStats(),
        api.getReferralLeaderboard(),
      ]);

      if (settingsRes?.success) {
        const s = settingsRes.data || {};
        setSettings({
          isActive: !!s.isActive,
          pointsPerReferral: s.pointsPerReferral ?? 10,
          description: s.description || '',
        });
      }

      if (statsRes?.success) {
        const d = statsRes.data || {};
        setStats(d);
        setRecent(d.recent || []);
      }

      if (leaderboardRes?.success) {
        setLeaderboard(leaderboardRes.data || []);
      }
    } catch (err) {
      console.error(err);
      setError('Erreur lors du chargement du parrainage.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    reloadTimerRef.current = setTimeout(() => {
      loadData({ silent: true });
    }, 400);
  }, [loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let unsubReferral;
    let unsubConnect;

    const onReferralUpdate = (payload) => {
      if (payload?.stats) {
        const { recent, ...statsOnly } = payload.stats;
        setStats(statsOnly);
        if (Array.isArray(recent)) {
          setRecent(recent);
        }
      }
      scheduleReload();
    };

    const bindListeners = () => {
      unsubReferral = socketService.on('admin:referral_update', onReferralUpdate);
      unsubConnect = socketService.on('connect', () => {
        loadData({ silent: true });
      });
    };

    bindListeners();
    socketService.connect().then(() => {
      unsubReferral?.();
      unsubConnect?.();
      bindListeners();
    }).catch(() => {});

    return () => {
      unsubReferral?.();
      unsubConnect?.();
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    };
  }, [scheduleReload, loadData]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const response = await api.updateReferralSettings(settings);
      if (response?.success) {
        setSuccessMessage('Paramètres enregistrés');
        await loadData();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(response?.message || 'Erreur');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = () => {
    setSettings((prev) => ({ ...prev, isActive: !prev.isActive }));
  };

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiUsers className="w-8 h-8 mr-3" />
            Parrainage déménageurs
          </h1>
          <p className="text-gray-400">
            Règles du programme et classement par points
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="mt-4 sm:mt-0 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 font-semibold flex items-center"
        >
          <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <FiAlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)}><FiX /></button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg flex items-center">
          <FiCheck className="w-5 h-5 mr-2" />
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
          <p className="text-sm text-gray-400 uppercase mb-2">Programme</p>
          <p className={`text-2xl font-bold ${stats.isActive ? 'text-green-400' : 'text-red-400'}`}>
            {stats.isActive ? 'Actif' : 'Inactif'}
          </p>
        </div>
        <div className="bg-gradient-to-br from-blue-600/10 to-indigo-600/10 border border-blue-600/30 rounded-xl p-6">
          <p className="text-sm text-gray-400 uppercase mb-2">Parrainages</p>
          <p className="text-4xl font-bold text-blue-400">{stats.totalReferrals || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-purple-600/10 to-pink-600/10 border border-purple-600/30 rounded-xl p-6">
          <p className="text-sm text-gray-400 uppercase mb-2">Points distribués</p>
          <p className="text-4xl font-bold text-purple-400">{stats.totalPointsAwarded || 0}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 border border-green-600/30 rounded-xl p-6">
          <p className="text-sm text-gray-400 uppercase mb-2">Parrains actifs</p>
          <p className="text-4xl font-bold text-green-400">{stats.activeReferrers || 0}</p>
        </div>
      </div>

      <form
        onSubmit={handleSaveSettings}
        className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 space-y-5"
      >
        <h2 className="text-xl font-bold text-yellow-400">Règles du parrainage</h2>

        <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700">
          <div>
            <p className="font-semibold text-white">Activer le programme</p>
            <p className="text-sm text-gray-400">
              Les nouveaux déménageurs verront la carte de saisie du code après inscription
            </p>
          </div>
          <button type="button" onClick={toggleActive} className="text-3xl text-yellow-400">
            {settings.isActive ? <FiToggleRight /> : <FiToggleLeft />}
          </button>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-2">
            Points attribués au parrain (par filleul inscrit)
          </label>
          <input
            type="number"
            min="0"
            value={settings.pointsPerReferral}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                pointsPerReferral: parseInt(e.target.value, 10) || 0,
              }))
            }
            className="w-full max-w-xs px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-300 mb-2">
            Description / raison (affichée aux déménageurs)
          </label>
          <textarea
            rows={3}
            value={settings.description}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Ex: Gagnez des points en parrainant un collègue déménageur"
            className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg"
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-yellow-600 text-gray-900 rounded-lg font-bold hover:bg-yellow-500 disabled:opacity-50"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les règles'}
        </button>
      </form>

      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-yellow-400 flex items-center">
            <FiAward className="mr-2" />
            Classement des déménageurs (points)
          </h2>
        </div>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-yellow-500" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-900/80 text-gray-400 text-sm uppercase">
                <tr>
                  <th className="px-6 py-4">Rang</th>
                  <th className="px-6 py-4">Déménageur</th>
                  <th className="px-6 py-4">Code</th>
                  <th className="px-6 py-4">Points</th>
                  <th className="px-6 py-4">Filleuls</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      Aucun déménageur pour le moment
                    </td>
                  </tr>
                ) : (
                  leaderboard.map((row) => (
                    <tr key={row.id} className="border-t border-gray-700/50 hover:bg-gray-800/50">
                      <td className="px-6 py-4 font-bold text-yellow-400">#{row.rank}</td>
                      <td className="px-6 py-4">
                        <div className="font-semibold">{row.fullName || '—'}</div>
                        <div className="text-sm text-gray-500">{row.phone}</div>
                      </td>
                      <td className="px-6 py-4 font-mono text-yellow-300">{row.referralCode || '—'}</td>
                      <td className="px-6 py-4 text-2xl font-bold text-purple-400">{row.referralPoints}</td>
                      <td className="px-6 py-4">{row.referralsCount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {recent.length > 0 && (
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-200 mb-4">Derniers parrainages</h3>
          <div className="space-y-3">
            {recent.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap justify-between gap-2 p-3 bg-gray-900/50 rounded-lg border border-gray-700"
              >
                <span>
                  <strong>{item.referrerName}</strong> ← {item.referredName}
                </span>
                <span className="text-yellow-400 font-semibold">+{item.pointsAwarded} pts</span>
                <span className="text-sm text-gray-500">
                  {item.createdAt ? new Date(item.createdAt).toLocaleString('fr-FR') : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReferrals;
